<template>
  <div>
    <button
      v-if="props.showCloseButton"
      style="width: 12.5em; min-height: 3em; margin-bottom: 10px"
      class="btn btn-lg btn-default"
      data-test="session-close-button"
      @click="close"
      type="button"
    >
      Close
    </button>
    &nbsp;
    <div class="hidden-lg hidden-md" style="height: 10px"></div>
    <button
      @click="complete()"
      ref="completeButton"
      class="complete-button btn btn-lg btn-primary"
      :class="{
        'completed': props.buttonProps.completed,
        'already-completed': props.buttonProps.currentStepWasAlreadyComplete,
      }"
      data-test="session-complete-button"
      v-if="displayButton"
      type="button"
    >
      <span class="tick-icon glyphicon glyphicon-ok" data-test="tick-ok-icon"></span>
      <span class="complete-button-text" data-test="complete-button-text">{{
        props.buttonProps.completeButtonText
      }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  CmsPersonalPathwayStep,
  CmsSessionWithSlides,
  CmsTeamPathwayStep,
} from "../../services/cms/cms-type-definitions";

export type CmsSessionDoc = CmsSessionWithSlides | CmsTeamPathwayStep | CmsPersonalPathwayStep;

type SessionCompleteButtonProps = {
  ready?: boolean;
  canCompletePathwayStep?: boolean;
  completed?: boolean;
  currentStepWasAlreadyComplete?: boolean;
  isForPathwayStep: boolean;
  completeButtonText: string;
  buttonLink?: string;
};

const props = defineProps<{ buttonProps: SessionCompleteButtonProps; showCloseButton: boolean }>();

const emit = defineEmits(["close", "complete"]);

const displayButton = computed<boolean>(() => {
  const { buttonProps } = props;

  if (buttonProps.isForPathwayStep) {
    return !!buttonProps.ready && (!!buttonProps.canCompletePathwayStep || !!buttonProps.completed);
  }

  return true;
});

const close = (): void => {
  emit("close");
};

const complete = (): void => {
  emit("complete");
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.complete-button {
  width: 12.5em; // Give an explicit width so we can animate it easily
  min-width: 3em;
  min-height: 3em;
  position: relative;
  user-select: none !important;
  outline: none !important;
  display: block;
  margin: 0 auto;

  .tick-icon {
    color: white !important;
    font-size: 32px;
    width: 0;
    overflow: hidden;
    margin-left: 0.3em;
    text-align: left;
    position: absolute;
    top: 0.3em;
    left: 0;
    height: 100%;
  }

  @total-duration: 2s;
  @first-duration: 1.5s;
  @second-duration: @total-duration - @first-duration;
  @text-fade-duration: 0.3s;

  &.completed {
    animation-name: complete-button-reduce, complete-button-to-tick;
    animation-duration: @first-duration, @second-duration;
    animation-delay: 0s, @first-duration;
    animation-iteration-count: 1, 1;
    animation-fill-mode: forwards, forwards;
    animation-timing-function: linear, linear;
    pointer-events: none;

    &.already-completed {
      animation-duration: 0s;
      animation-delay: 0s;
    }

    .complete-button-text {
      animation-name: fade-out;
      animation-duration: @text-fade-duration;
      animation-fill-mode: forwards;
    }

    .tick-icon {
      animation-name: complete-tick;
      animation-duration: @second-duration;
      animation-timing-function: cubic-bezier(0.68, 0, 0.45, 0.92);
      animation-delay: @first-duration;
      animation-fill-mode: forwards;
    }

    @keyframes fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes fade-out {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    @keyframes complete-tick {
      from {
        width: 0;
        overflow: hidden;
      }
      to {
        width: 1em;
        overflow: visible;
      }
    }

    @keyframes complete-button-reduce {
      from {
        width: 12.5em;
      }
      to {
        width: 34px;
        padding: 0;
      }
    }

    @keyframes complete-button-to-tick {
      from {
        //
      }
      to {
        background-color: @innovator-color;
        border-color: @innovator-color;
        border-radius: 50%;
      }
    }
  }
}
</style>
